import { Box, Typography, Avatar, Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';
import { VendorCommentItem } from '../../types';
import InactiveUserName from '../../components/InactiveUserName';

const useStyles = makeStyles()((theme: Theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0',
    fontFamily: 'Poppins',
    color: '#ffffff',
  },

  commentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginTop: '15px',
    maxHeight: '200px',
    overflowY: 'auto' as const,
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '6px',
    padding: '12px',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },

  commentItem: {
    display: 'flex',
    gap: '14px',
    padding: '8px 0px',

    '&:last-child': {
      paddingBottom: 0,
    },
  },

  avatar: {
    width: '40px',
    height: '40px',
    fontSize: '14px',
    fontWeight: 600,
    backgroundColor: theme.palette.info.main,
    color: '#FFFFFF',
  },

  commentContent: {
    flex: 1,
  },

  nameRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: '6px',
    flexWrap: 'nowrap',
    marginBottom: '2px',
  },

  commenterName: {
    fontWeight: 600,
    fontSize: '16px',
    color: '#FFFFFF',
    lineHeight: 1.2,
  },

  commenterRole: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    lineHeight: 1.2,
  },

  commentDate: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.65)',
    marginBottom: '8px',
  },

  commentText: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontWeight: '500',
    lineHeight: '120%',
    letterSpacing: '0px',
    whiteSpace: 'pre-wrap' as const,
    wordBreak: 'break-word' as const,
  },

  emptyText: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.5)',
    fontFamily: 'Inter',
  },

  centerBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '16px 0',
  },
}));

interface VendorCommentsSectionProps {
  comments: VendorCommentItem[];
}

const getInitials = (name: string): string => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const VendorCommentsSection = ({ comments }: VendorCommentsSectionProps) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.container}>
      {comments.length > 0 && (
        <Box className={classes.commentsList}>
          {comments.map((item) => (
            <Box key={item.id} className={classes.commentItem}>
              <Avatar className={classes.avatar}>{getInitials(item.addedByName)}</Avatar>

              <Box className={classes.commentContent}>
                <Box className={classes.nameRow}>
                  <InactiveUserName isInactive={item.addedByIsDeactivated}>
                    <Typography className={classes.commenterName}>{item.addedByName}</Typography>
                  </InactiveUserName>
                  <Typography className={classes.commenterRole}>{item.addedByRole}</Typography>
                </Box>

                <Typography className={classes.commentDate}>
                  {moment(item.addedAt).format('MMM DD, YYYY · h:mm A')}
                </Typography>

                <Typography className={classes.commentText}>
                  {item.comment}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default VendorCommentsSection;
